/**
 * Google Tag Manager DataLayer Utilities
 * 
 * This module provides helper functions for pushing e-commerce events
 * to the Google Tag Manager dataLayer for GA4 Enhanced E-commerce tracking.
 * 
 * Reference: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 */

declare global {
  interface Window {
    dataLayer: any[];
  }
}

// Ensure dataLayer exists
const getDataLayer = (): any[] => {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

/**
 * Push an event to the dataLayer
 */
export const pushToDataLayer = (data: Record<string, any>): void => {
  const dataLayer = getDataLayer();
  dataLayer.push(data);
  console.log('GTM DataLayer Push:', data);
};

/**
 * Clear previous ecommerce object to prevent data bleeding
 */
export const clearEcommerce = (): void => {
  pushToDataLayer({ ecommerce: null });
};

/**
 * Product item interface for GA4 e-commerce
 */
interface GA4Item {
  item_id: string;
  item_name: string;
  affiliation?: string;
  coupon?: string;
  discount?: number;
  index?: number;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_list_id?: string;
  item_list_name?: string;
  item_variant?: string;
  location_id?: string;
  price?: number;
  quantity?: number;
}

/**
 * Convert product to GA4 item format
 */
export const productToGA4Item = (product: {
  id: string | number;
  name: string;
  price: number;
  category?: string;
  brand?: string;
  variant?: string;
  quantity?: number;
  position?: number;
  listId?: string;
  listName?: string;
}): GA4Item => {
  return {
    item_id: product.id.toString(),
    item_name: product.name,
    price: product.price,
    quantity: product.quantity || 1,
    item_category: product.category || '',
    item_brand: product.brand || '',
    item_variant: product.variant || '',
    index: product.position,
    item_list_id: product.listId,
    item_list_name: product.listName,
  };
};

/**
 * GTM E-commerce Events for GA4
 */
export const gtmEvents = {
  /**
   * Track when a user views a product listing (category page, search results)
   */
  viewItemList: (products: Array<{
    id: string | number;
    name: string;
    price: number;
    category?: string;
    brand?: string;
    position?: number;
  }>, listId: string, listName: string) => {
    clearEcommerce();
    pushToDataLayer({
      event: 'view_item_list',
      ecommerce: {
        item_list_id: listId,
        item_list_name: listName,
        items: products.map((product, index) => productToGA4Item({
          ...product,
          position: product.position || index,
          listId,
          listName,
        })),
      },
    });
  },

  /**
   * Track when a user clicks on a product in a listing
   */
  selectItem: (product: {
    id: string | number;
    name: string;
    price: number;
    category?: string;
    brand?: string;
  }, listId?: string, listName?: string) => {
    clearEcommerce();
    pushToDataLayer({
      event: 'select_item',
      ecommerce: {
        item_list_id: listId,
        item_list_name: listName,
        items: [productToGA4Item({ ...product, listId, listName })],
      },
    });
  },

  /**
   * Track when a user views a product detail page
   */
  viewItem: (product: {
    id: string | number;
    name: string;
    price: number;
    category?: string;
    brand?: string;
    variant?: string;
  }) => {
    clearEcommerce();
    pushToDataLayer({
      event: 'view_item',
      ecommerce: {
        currency: 'BDT',
        value: product.price,
        items: [productToGA4Item(product)],
      },
    });
  },

  /**
   * Track when a user adds an item to cart
   */
  addToCart: (product: {
    id: string | number;
    name: string;
    price: number;
    quantity?: number;
    category?: string;
    brand?: string;
    variant?: string;
  }) => {
    clearEcommerce();
    pushToDataLayer({
      event: 'add_to_cart',
      ecommerce: {
        currency: 'BDT',
        value: product.price * (product.quantity || 1),
        items: [productToGA4Item(product)],
      },
    });
  },

  /**
   * Track when a user removes an item from cart
   */
  removeFromCart: (product: {
    id: string | number;
    name: string;
    price: number;
    quantity?: number;
    category?: string;
    brand?: string;
  }) => {
    clearEcommerce();
    pushToDataLayer({
      event: 'remove_from_cart',
      ecommerce: {
        currency: 'BDT',
        value: product.price * (product.quantity || 1),
        items: [productToGA4Item(product)],
      },
    });
  },

  /**
   * Track when a user views their cart
   */
  viewCart: (items: Array<{
    id: string | number;
    name: string;
    price: number;
    quantity: number;
    category?: string;
    brand?: string;
  }>, totalValue: number) => {
    clearEcommerce();
    pushToDataLayer({
      event: 'view_cart',
      ecommerce: {
        currency: 'BDT',
        value: totalValue,
        items: items.map(item => productToGA4Item(item)),
      },
    });
  },

  /**
   * Track when a user begins checkout
   */
  beginCheckout: (items: Array<{
    id: string | number;
    name: string;
    price: number;
    quantity: number;
    category?: string;
    brand?: string;
  }>, totalValue: number, coupon?: string) => {
    clearEcommerce();
    pushToDataLayer({
      event: 'begin_checkout',
      ecommerce: {
        currency: 'BDT',
        value: totalValue,
        coupon: coupon,
        items: items.map(item => productToGA4Item(item)),
      },
    });
  },

  /**
   * Track when a user adds shipping info during checkout
   */
  addShippingInfo: (items: Array<{
    id: string | number;
    name: string;
    price: number;
    quantity: number;
  }>, totalValue: number, shippingTier: string, coupon?: string) => {
    clearEcommerce();
    pushToDataLayer({
      event: 'add_shipping_info',
      ecommerce: {
        currency: 'BDT',
        value: totalValue,
        coupon: coupon,
        shipping_tier: shippingTier,
        items: items.map(item => productToGA4Item(item)),
      },
    });
  },

  /**
   * Track when a user adds payment info during checkout
   */
  addPaymentInfo: (items: Array<{
    id: string | number;
    name: string;
    price: number;
    quantity: number;
  }>, totalValue: number, paymentType: string, coupon?: string) => {
    clearEcommerce();
    pushToDataLayer({
      event: 'add_payment_info',
      ecommerce: {
        currency: 'BDT',
        value: totalValue,
        coupon: coupon,
        payment_type: paymentType,
        items: items.map(item => productToGA4Item(item)),
      },
    });
  },

  /**
   * Track a successful purchase
   */
  purchase: (order: {
    orderId: string;
    totalValue: number;
    shipping?: number;
    tax?: number;
    coupon?: string;
    items: Array<{
      id: string | number;
      name: string;
      price: number;
      quantity: number;
      category?: string;
      brand?: string;
    }>;
  }) => {
    clearEcommerce();
    pushToDataLayer({
      event: 'purchase',
      ecommerce: {
        transaction_id: order.orderId,
        currency: 'BDT',
        value: order.totalValue,
        shipping: order.shipping || 0,
        tax: order.tax || 0,
        coupon: order.coupon,
        items: order.items.map(item => productToGA4Item(item)),
      },
    });
  },

  /**
   * Track when a user adds an item to wishlist
   */
  addToWishlist: (product: {
    id: string | number;
    name: string;
    price: number;
    category?: string;
    brand?: string;
  }) => {
    clearEcommerce();
    pushToDataLayer({
      event: 'add_to_wishlist',
      ecommerce: {
        currency: 'BDT',
        value: product.price,
        items: [productToGA4Item(product)],
      },
    });
  },

  /**
   * Track search queries
   */
  search: (searchTerm: string) => {
    pushToDataLayer({
      event: 'search',
      search_term: searchTerm,
    });
  },

  /**
   * Track when a user signs up
   */
  signUp: (method: string) => {
    pushToDataLayer({
      event: 'sign_up',
      method: method,
    });
  },

  /**
   * Track when a user logs in
   */
  login: (method: string) => {
    pushToDataLayer({
      event: 'login',
      method: method,
    });
  },

  /**
   * Track form submissions (lead generation)
   */
  generateLead: (value?: number, currency?: string) => {
    pushToDataLayer({
      event: 'generate_lead',
      value: value || 0,
      currency: currency || 'BDT',
    });
  },

  /**
   * Track refunds
   */
  refund: (orderId: string, totalValue: number, items?: Array<{
    id: string | number;
    quantity: number;
  }>) => {
    clearEcommerce();
    const data: Record<string, any> = {
      event: 'refund',
      ecommerce: {
        transaction_id: orderId,
        currency: 'BDT',
        value: totalValue,
      },
    };
    
    if (items && items.length > 0) {
      data.ecommerce.items = items.map(item => ({
        item_id: item.id.toString(),
        quantity: item.quantity,
      }));
    }
    
    pushToDataLayer(data);
  },

  /**
   * Track promotions viewed
   */
  viewPromotion: (promotions: Array<{
    id: string;
    name: string;
    creativeName?: string;
    creativeSlot?: string;
    position?: string;
  }>) => {
    clearEcommerce();
    pushToDataLayer({
      event: 'view_promotion',
      ecommerce: {
        items: promotions.map(promo => ({
          promotion_id: promo.id,
          promotion_name: promo.name,
          creative_name: promo.creativeName,
          creative_slot: promo.creativeSlot,
          location_id: promo.position,
        })),
      },
    });
  },

  /**
   * Track promotion clicks
   */
  selectPromotion: (promotion: {
    id: string;
    name: string;
    creativeName?: string;
    creativeSlot?: string;
    position?: string;
  }) => {
    clearEcommerce();
    pushToDataLayer({
      event: 'select_promotion',
      ecommerce: {
        items: [{
          promotion_id: promotion.id,
          promotion_name: promotion.name,
          creative_name: promotion.creativeName,
          creative_slot: promotion.creativeSlot,
          location_id: promotion.position,
        }],
      },
    });
  },
};

/**
 * Push custom event to dataLayer
 */
export const trackCustomGTMEvent = (eventName: string, eventParams: Record<string, any> = {}) => {
  pushToDataLayer({
    event: eventName,
    ...eventParams,
  });
};

export default gtmEvents;
